import React, { FC, JSX, useCallback, useMemo } from 'react';

import { isNumber } from 'lodash';
import { observer } from 'mobx-react-lite';

import {
	BUTTON_TYPES,
	DropdownOptionType,
	EvButton,
	EvConfirm,
	EvCSVExport,
	EvDropdownMenu,
	EvLift,
	EvLinkTypes,
	EvSpinner,
	EvSvgIcon,
	EvTableColumn,
	MoreHorizontal,
	SORT_ORDER,
	TOAST_STATUS
} from '@evinced-private/ui-common';

import { SITE_SCANNER_APP_ID } from '../../consts/dom-consts';
import { formatDateTimeForFileName } from '../../helpers/DateFormatHelper';
import { RoutesHelper } from '../../helpers/RoutesHelper';
import { ScanCrawlStateHelper } from '../../helpers/ScanCrawlStateHelper';
import { roundScore } from '../../helpers/ScoreHelper';
import { ICollection } from '../../interfaces/Collection';
import { IProperty } from '../../interfaces/Property';
import { IScan } from '../../interfaces/Scan';
import { IServerError } from '../../interfaces/ServerError';
import { IUrlSet } from '../../interfaces/UrlSet';
import { PROPERTIES_PAGE_FILTERS_ID } from '../../pages/properties/helpers/PropertiesPageFiltersHelper';
import {
	MY_COLLECTION_NAME,
	useCollections
} from '../../providers/collections/CollectionsProvider';
import { useCommonFiltersStore } from '../../providers/commonFiltersProvider/CommonFiltersProvider';
import { useConfiguration } from '../../providers/configurationProvider/ConfigurationProvider';
import { useDashboard } from '../../providers/dashboard/DashboardProvider';
import { useNotifications } from '../../providers/notificationsProvider/NotificationsConsumer';
import { useUserTenant } from '../../providers/userTenantProvider/UserTenantProvider';
import {
	addPropertyToCollectionById,
	deletePropertyFromCollectionById
} from '../../services/CollectionsService';
import { SiteScannerUiToggles } from '../../services/LocalTogglesService';
import { logger } from '../../services/Logger';
import { deleteProperty } from '../../services/PropertiesService';
import { URL_SET_SOURCES } from '../../types/UrlSetSources';
import { EvLinkLocal } from '../common/ev-link-local/EvLinkLocal';
import { renderNumberCellFormatter } from '../common/ev-table/cell-formatters/numberCellFormatter';
import { headers } from '../common/site-scanner-table/SiteScanerTable.constants';
import { SiteScannerTable } from '../common/site-scanner-table/SiteScannerTable';
import { getPropertiesCsvData } from '../common/site-scanner-table/SiteScannerTable.utils';
import { JobsStatusAction } from '../jobs-status-row-action/JobsStatusAction';
import { NoDataInCollection } from '../no-data-in-collection/NoDataInCollection';
import { StartCrawlPopup } from '../start-crawl-popup/StartCrawlPopup';
import { StartScanPopup } from '../start-scan-popup/StartScanPopup';

import { DATA_AUTOMATION_IDS, LABELS } from './PropertiesTable.constants';
import { getFilteredTableData, renderDateStatusCol } from './PropertiesTableHelper';

import './PropertiesTable.scss';

export const PROPERTIES_PAGE_TABLE_ID = 'properties-page-table';
const REMOVE_FROM_TEXT = 'Remove from';
const ADD_TO_TEXT = 'Add to';
const PROPERTY_REMOVED_TEXT = 'Property removed from collection';
const PROPERTY_ADDED_TEXT = 'Property added to collection';

interface IPropertiesTableProps {
	properties: IProperty[];
	isLoading: boolean;
	refetch: () => void;
	handleResetFilters: () => void;
}

const PropertiesTableV2Raw: FC<IPropertiesTableProps> = ({
	properties,
	isLoading,
	refetch,
	handleResetFilters
}) => {
	const notificationsContext = useNotifications();
	const commonFiltersStore = useCommonFiltersStore();
	const { refreshDashboardData } = useDashboard();
	const { isReadOnlyUser, tenant } = useUserTenant();
	const { getToggle } = useConfiguration();
	const { propertyCollections, myCollection } = useCollections();

	const isMultipleCollectionsEnabled = getToggle(SiteScannerUiToggles.ENABLE_MULTIPLE_COLLECTIONS);

	const handleCsvExportError = useCallback(() => {
		notificationsContext.toast({
			show: true,
			status: TOAST_STATUS.FAIL,
			announcement: LABELS.CSV_EXPORT_ERROR
		});
	}, [notificationsContext]);

	const appliedFilters = commonFiltersStore.getFilterValue(PROPERTIES_PAGE_FILTERS_ID);

	const filteredTableData = useMemo(() => {
		return getFilteredTableData(properties, appliedFilters);
	}, [properties, appliedFilters]);

	const isNoDataInSelectedCollection = useMemo(
		() => propertyCollections?.length && !filteredTableData.length,
		[propertyCollections, filteredTableData]
	);

	const onDeleteProperty = useCallback(
		async (idToDelete: string, closeModal: () => void) => {
			try {
				await deleteProperty(idToDelete);
				await refetch();
			} catch (err) {
				const msg = `An error while trying to delete property ${idToDelete}.`;
				logger.error(msg, err);
				notificationsContext.alert({
					errorMessage: msg,
					serverError: err as IServerError,
					onOK: () => {
						refetch();
					}
				});
			} finally {
				closeModal();
			}
		},
		[notificationsContext, refetch]
	);

	const renderScanPopup = (property, accessibilityProps, close): JSX.Element => {
		return (
			<StartScanPopup
				triggerButtonProps={{
					type: BUTTON_TYPES.ICON,
					title: 'Scan Now',
					children: 'Scan',
					accessibilityProps
				}}
				onScanCreated={() => {
					logger.info(`Scan created for property ${property.id}`);
					refetch();
					close();
				}}
				propertyId={property.id}
				urlSet={property.lastUrlSet}
			/>
		);
	};

	const renderCollectionAction = (
		property: IProperty,
		accessibilityProps: Record<string, string>,
		onDropdownOpenStateChange: (isOpen: boolean) => void
	): JSX.Element => {
		const { id, collections } = property;
		const isPropertyAlreadyInCollection = myCollection
			? collections?.some((collection: ICollection) => collection.id === myCollection?.id)
			: false;
		const buttonText = `${
			isPropertyAlreadyInCollection ? REMOVE_FROM_TEXT : ADD_TO_TEXT
		} "${MY_COLLECTION_NAME}"`;

		const clickAndNotify = async (): Promise<void> => {
			const action = isPropertyAlreadyInCollection
				? deletePropertyFromCollectionById
				: addPropertyToCollectionById;

			try {
				await action(myCollection.id, id);
				onDropdownOpenStateChange(false);
				notifyAction(true);
				refreshDashboardData();
				refetch();
			} catch (error) {
				notifyAction(false, error);
			}
		};

		const notifyAction = (isSuccess: boolean, error?: IServerError): void => {
			const announcement = isPropertyAlreadyInCollection
				? PROPERTY_REMOVED_TEXT
				: PROPERTY_ADDED_TEXT;
			if (isSuccess) {
				notificationsContext.toast({
					show: true,
					status: TOAST_STATUS.SUCCESS,
					announcement
				});
			} else {
				notificationsContext.alert({
					errorMessage: `Error occurred while trying to ${buttonText}`,
					serverError: error
				});
			}
		};

		return (
			<EvButton
				type={BUTTON_TYPES.ICON}
				accessibilityProps={accessibilityProps}
				onClick={clickAndNotify}
			>
				{buttonText}
			</EvButton>
		);
	};

	const renderCrawlPopup = (property, accessibilityProps, close): JSX.Element => {
		if (property.lastUrlSet && property.lastUrlSet.source === URL_SET_SOURCES.UPLOAD) {
			return (
				<EvButton
					type={BUTTON_TYPES.ICON}
					customTooltip={{ renderTooltip: true, place: 'left' }}
					disabled
					ariaLabel="Crawl is disabled in List Mode. The property is configured with a predefined list of URLs."
					accessibilityProps={accessibilityProps}
				>
					Crawl
				</EvButton>
			);
		}

		return (
			<StartCrawlPopup
				triggerButtonProps={{
					type: BUTTON_TYPES.ICON,
					title: 'Crawl Now',
					children: 'Crawl',
					accessibilityProps
				}}
				onCrawlCreated={() => {
					refetch();
					close();
				}}
				propertyId={property.id}
			/>
		);
	};

	const renderConfirmDeletePropertyPopup = (row, accessibilityProps): JSX.Element => {
		return (
			<EvConfirm
				appElement={SITE_SCANNER_APP_ID}
				triggerButtonProps={{
					type: BUTTON_TYPES.ICON,
					title: 'Archive property',
					children: 'Archive property',
					accessibilityProps
				}}
				approveButtonText="Archive"
				title="Confirm archive property"
				promptMessage={`Are you sure you want to archive ${row.name} ?`}
				onConfirm={(closeModal) => {
					onDeleteProperty(row.id, closeModal);
				}}
			/>
		);
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const renderActionsCell = (cell, property): any => {
		const isViewScanResultsEnabled =
			property.lastScan && ScanCrawlStateHelper.isScanResultsReady(property.lastScan.state);

		return (
			<div className="actions-cell">
				<EvDropdownMenu
					id={`menu-${property.id}`}
					triggerButtonProps={{
						type: BUTTON_TYPES.ICON,
						ariaHaspopup: 'menu',
						title: `${property.name} actions`,
						children: (
							<div className="menu-trigger-icon">
								<EvSvgIcon icon={MoreHorizontal} isSmall />
							</div>
						)
					}}
					options={[
						!isReadOnlyUser() && {
							id: 'property-settings',
							url: RoutesHelper.getPropertySettingsPagePath(property.id),
							title: 'Property settings',
							type: DropdownOptionType.LINK
						},
						// TODO: remove this block when multiple collections feature is fully integrated
						!isMultipleCollectionsEnabled && {
							id: 'collection-action',
							renderOption: (accessibilityProps, onDropdownOpenStateChange) => {
								return renderCollectionAction(
									property,
									accessibilityProps,
									onDropdownOpenStateChange
								);
							},
							type: DropdownOptionType.CUSTOM
						},
						{
							id: 'view-scan-results',
							url:
								isViewScanResultsEnabled &&
								RoutesHelper.getScanResultsPagePath(property.id, property.lastScan.id),
							title: 'Scan results',
							disabled: !isViewScanResultsEnabled,
							type: DropdownOptionType.LINK
						},
						!isReadOnlyUser() && {
							id: 'crawl-property',
							renderOption: (accessibilityProps, close) => {
								return renderCrawlPopup(property, accessibilityProps, close);
							},
							type: DropdownOptionType.CUSTOM
						},
						!isReadOnlyUser() && {
							id: 'scan-property',
							renderOption: (accessibilityProps, close) => {
								return renderScanPopup(property, accessibilityProps, close);
							},
							type: DropdownOptionType.CUSTOM
						},
						!isReadOnlyUser() && {
							id: 'delete-property',
							renderOption: (accessibilityProps) => {
								return renderConfirmDeletePropertyPopup(property, accessibilityProps);
							},
							type: DropdownOptionType.CUSTOM
						}
					].filter((e) => e)}
				/>
			</div>
		);
	};

	const renderLiftCell = (cell, property: IProperty): JSX.Element => {
		const { scoreResult } = property;
		const liftPercentage = scoreResult?.liftPercentage;
		const roundedValue = (Math.ceil(liftPercentage * 10) / 10).toFixed(1);
		let roundedValueNumber = Number(roundedValue);
		if (
			!liftPercentage ||
			!isNumber(liftPercentage) ||
			roundedValueNumber === 0 ||
			!roundedValueNumber
		) {
			roundedValueNumber = null;
		}
		const isPositiveChange: boolean = roundedValueNumber > 0;
		return <EvLift liftNumber={liftPercentage} small success={isPositiveChange} />;
	};

	const getColumns = (): EvTableColumn[] => {
		const columns = [
			{
				dataField: 'name',
				text: 'Name',
				style: { maxWidth: '240px', width: '240px' },
				headerStyle: { maxWidth: '240px', width: '240px' },
				headerClasses: 'filter-cell',
				// eslint-disable-next-line react/no-unstable-nested-components
				formatter: (cell, row) => {
					return (
						<EvLinkLocal
							id={cell}
							type={EvLinkTypes.DARK}
							url={RoutesHelper.getPropertyPagePath(row.id)}
							linkText={cell}
							pageId={PROPERTIES_PAGE_TABLE_ID}
						/>
					);
				},
				sort: true
			},
			{
				dataField: 'scoreResult.score',
				text: 'Score',
				style: { maxWidth: '100px', width: '100px' },
				headerStyle: { maxWidth: '100px', width: '100px' },
				sortValue: (cell) => (cell && isNumber(cell) ? roundScore(cell) : 0),
				// eslint-disable-next-line react/no-unstable-nested-components
				formatter: (cell: IScan, property: IProperty) => {
					if (property?.scoreResult?.score === undefined) {
						return <div>N/A</div>;
					}
					const formattedScore = roundScore(property?.scoreResult?.score || 0);
					return renderNumberCellFormatter(formattedScore);
				},

				sort: true
			},
			{
				dataField: 'scoreResult.liftPercentage',
				text: 'Score Change',
				style: { maxWidth: '150px', width: '150px' },
				headerStyle: { maxWidth: '150px', width: '150px' },
				sortValue: (cell) => (cell && isNumber(cell) ? cell : 0),
				sort: true,
				formatter: (cell: IScan, property: IProperty) => {
					return renderLiftCell(cell, property);
				}
			},
			{
				dataField: 'lastUrlSet.totalUrlsCount',
				text: 'Pages',
				style: { maxWidth: '150px', width: '150px' },
				headerStyle: { maxWidth: '150px', width: '150px' },
				sort: true,
				formatter: renderNumberCellFormatter
			},
			{
				dataField: 'lastScan.issuesCount',
				text: 'Issues',
				style: { maxWidth: '150px', width: '150px' },
				headerStyle: { maxWidth: '150px', width: '150px' },
				sort: true,
				formatter: renderNumberCellFormatter
			},
			{
				dataField: 'lastUrlSet',
				text: 'Last crawl',
				style: { maxWidth: '170px', width: '170px' },
				headerStyle: { maxWidth: '170px', width: '170px' },
				sortValue: (cell: IUrlSet) => (cell && cell.createdTime) || '',
				sort: true,
				formatter: (cell: IUrlSet) => {
					return renderDateStatusCol(cell, false);
				}
			},
			{
				dataField: 'lastScan',
				text: 'Last scan',
				style: { maxWidth: '170px', width: '170px' },
				headerStyle: { maxWidth: '170px', width: '170px' },
				sortValue: (cell) => (cell && cell.createdTime) || '',
				sort: true,
				formatter: (cell: IScan) => {
					return renderDateStatusCol(cell, true);
				}
			},
			{
				dataField: 'actions',
				text: 'Actions',
				style: { maxWidth: '80px', width: '80px' },
				headerStyle: { maxWidth: '80px', width: '80px' },
				classes: 'actions-cell-td',
				formatter: renderActionsCell
			}
		];

		return columns;
	};

	const csvExportData = useMemo(() => {
		return getPropertiesCsvData(filteredTableData);
	}, [filteredTableData]);

	if (isLoading) {
		return <EvSpinner />;
	}
	if (isNoDataInSelectedCollection) {
		return <NoDataInCollection handleClearFilters={handleResetFilters} />;
	}

	return (
		<SiteScannerTable
			tableId={PROPERTIES_PAGE_TABLE_ID}
			totalCount={filteredTableData.length}
			tableData={filteredTableData}
			className="properties-table"
			columns={getColumns()}
			topRowActions={
				<>
					<JobsStatusAction properties={properties} />
					{csvExportData.length > 0 && (
						<div className="csv-export-button-container">
							<EvCSVExport
								staticData={csvExportData}
								triggerButtonProps={{
									title: LABELS.CSV_EXPORT_BUTTON,
									children: LABELS.CSV_EXPORT_BUTTON,
									loading: isLoading,
									dataAutomationId: DATA_AUTOMATION_IDS.CSV_EXPORT_BUTTON
								}}
								fileName={`${tenant?.name}_properties_${formatDateTimeForFileName()}`}
								headers={headers}
								onError={handleCsvExportError}
							/>
						</div>
					)}
				</>
			}
			alignActionsRight={false}
			isTableLoading={isLoading}
			options={{
				title: 'Properties',
				pagination: true,
				defaultSorted: [{ dataField: 'lastScan', order: SORT_ORDER.DESC }],
				dataType: 'properties',
				caption: 'Properties table'
			}}
		/>
	);
};

export const PropertiesTableV2 = observer(PropertiesTableV2Raw);
